// Material UI
import { PopoverOrigin } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select, { SelectProps } from "@material-ui/core/Select";
import React, { useRef, useState } from "react";
// Assets
import { ReactComponent as downArrowIcon } from "../../assets/svg/DownArrow.svg";
import { ReactComponent as CloseIcon } from "../../assets/svg/CloseIcon.svg";
// Styles
import { anchorOriginDefault, selectBoxStyles } from "./SelectBoxStyles";
import { EmptyIcon } from "../App/AppViewUtils";

interface SelectBoxProps extends SelectProps {
  fieldLabel?: string;
  label?: string;
  helperText?: string;
  value: any;
  handleChange: any;
  menuItems: Array<any>;
  error?: boolean;
  menuPaperProps?: any;
  id?: string;
  className?: string;
  anchorOrigin?: PopoverOrigin;
  hideIconComponent?: boolean;
  labelClass?: string;
  selectClass?: string;
  selectBoxMenuItemTextStyle?: any;
  isDeliveryFee?: any;
  isClearable?: boolean;
  // When true, passes `label` to the underlying Select so the outlined border renders a
  // notch/legend around the label (matching MultiSelectBox). Opt-in: many callers deliberately
  // hide the shrunk label (e.g. `hideShrinkedLabel`) and rely on a clean, un-notched border.
  showLabelNotch?: boolean;
}

const SelectBox = (props: SelectBoxProps) => {
  const {
    fieldLabel,
    label,
    labelClass,
    className,
    id = "",
    error,
    selectClass,
    value,
    handleChange,
    hideIconComponent,
    menuPaperProps,
    anchorOrigin,
    selectBoxMenuItemTextStyle,
    isClearable,
    isDeliveryFee,
    showLabelNotch,
    menuItems = [],
    ...rest
  } = props;

  const classes = selectBoxStyles();
  const anchorEl = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const showClearIcon = isClearable && value && !props.disabled;

  return (
    <>
      {fieldLabel && <div className={classes.fieldLabel}>{fieldLabel}</div>}
      <FormControl
        variant={isDeliveryFee ? "standard" : "outlined"}
        className={classes.formControl}
        error={error}
        disabled={props.disabled}
      >
        {label && (
          <InputLabel
            id={`${id}-${label}`}
            className={labelClass}
            disabled={props.disabled}
          >
            {label}
          </InputLabel>
        )}
        <Select
          {...rest}
          displayEmpty
          label={showLabelNotch ? label : undefined}
          className={`${className ?? ""} ${isOpen ? "open " : ""}${
            label ? "hasLabel" : ""
          } ${selectClass ?? ""} ${showClearIcon ? "hasClearIcon" : ""}`}
          ref={anchorEl}
          labelId={label ? `${id}-${label}` : ""}
          value={value}
          onChange={handleChange}
          onOpen={() => {
            setIsOpen(true);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          IconComponent={hideIconComponent ? EmptyIcon : downArrowIcon}
          MenuProps={{
            classes: {
              list: classes.menuPropsList,
              paper: menuPaperProps ?? classes.menuPropsPaper,
            },
            anchorEl: anchorEl.current,
            getContentAnchorEl: null,
            anchorOrigin: anchorOrigin || anchorOriginDefault,
          }}
        >
          {menuItems.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item?.displayName ?? item?.name ?? ""}
            </MenuItem>
          ))}
        </Select>
        {showClearIcon && (
          <CloseIcon
            className={classes.clearIcon}
            onClick={(e: any) => {
              e.stopPropagation();
              handleChange({ target: { name: rest.name, value: "" } });
            }}
          />
        )}
      </FormControl>
    </>
  );
};

export default React.memo(SelectBox);
